// @flow
import React, { type Node, useState, useEffect, useCallback } from 'react';
import throttle from 'lodash/throttle';
import debounce from 'lodash/debounce';
import cx from 'classnames';

const isDesktop = () => window.screen.width > 767;

const getStickyProps = (header, tableWrapper) => {
    // We only care about the alert position if the device can display the left-right
    // container split
    let isStuck = false;
    let bottom = 0;
    const bounding = tableWrapper?.getBoundingClientRect();

    if(!bounding) {
        return { isStuck, bottom };
    }

    if (isDesktop()) {
        const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
        let headerAlertsBottom = 0;

        const headerAlerts = document.querySelector('.nav-header-alerts');
        if (headerAlerts) {
            headerAlertsBottom = headerAlerts.getBoundingClientRect().bottom;
        }

        bottom = headerAlertsBottom > headerBottom ? headerAlertsBottom : headerBottom;
        isStuck = bounding.top < bottom && bounding.bottom > bottom;
        return { isStuck, bottom };
    }
    return {
        isStuck: bounding.top < bottom && bounding.bottom > bottom,
        bottom: header ? header.getBoundingClientRect().bottom : 0
    };
}

type PropTypes = {
    children: Node,
    scrollingContainerId: string,
    isFullView: boolean
};
function StickyTableHeaderHelper({
    children,
    scrollingContainerId,
    isFullView = false,
}: PropTypes) {
    const [isStuck, setIsStuck] = useState(false);
    const [scrollPosition, setScrollPosition] = useState(0);
    const [bottom, setBottom] = useState(0);
    const [header, setHeader] = useState(null);
    const [tableWrapper, setTableWrapper] = useState(null);

    const setSticky = useCallback(() => {
        const { isStuck: newIsStuck, bottom: newBottom } = getStickyProps(header, tableWrapper);
        setIsStuck(newIsStuck);
        setBottom(newBottom);
    }, [header, tableWrapper])
    const throttledSetSticky = throttle(setSticky, 100);
    const debouncedSetSticky = debounce(setSticky, 300);
    

    useEffect(() => {
        const handleWrapperScroll = () => setScrollPosition(tableWrapper?.scrollLeft || 0);

        if(tableWrapper) tableWrapper.addEventListener('scroll', handleWrapperScroll);
        setSticky();

        return () => {
            if(tableWrapper) tableWrapper.removeEventListener('scroll', handleWrapperScroll);
        }
    }, [tableWrapper, header, setSticky])

    useEffect(() => {
        setTableWrapper(document.querySelector('.per-availability-table-container'));
        setHeader(document.querySelector('header.nav-header-container'));

        window.addEventListener('resize', throttledSetSticky);
        document.addEventListener('scroll', setSticky);

        return () => {
            window.removeEventListener('resize', throttledSetSticky);
            document.removeEventListener('scroll', setSticky);
        }
    }, [setSticky, throttledSetSticky])

    useEffect(() => {
        const container = document.getElementById(scrollingContainerId);
        if (container && isDesktop()) {
            container.addEventListener('scroll', throttledSetSticky);
        }

        return () => {
            if (container) {
                container.removeEventListener('scroll', setSticky);
            }
        }
    }, [scrollingContainerId, setSticky, throttledSetSticky])

    useEffect(() => {
        // If the user clicks the "Expand Table" button, the action bar may resize,
        // so we're waiting an arbitrary amount of time for the page the re-render
        // and then calling setSticky() to recalculate headerAlertsBottom

        // TODO how to check if user clicked on expand table button?
        debouncedSetSticky()

        return () => debouncedSetSticky.cancel();
    })

    const style = cx('sticky-table-header-helper', {
        'sticky': isStuck,
        'expand': isFullView
    });

    return (
        <div className={style} style={{ top: bottom }}>
            <div className="sticky-table-header-helper-wrapper">
                <div style={{ transform: `translate3d(-${scrollPosition}px, 0, 0)` }}>
                    {children}
                </div>
            </div>
        </div>
    );
}

export default React.memo<PropTypes>(StickyTableHeaderHelper);
